/**
 * StrainsListPage Component
 *
 * List view for mushroom strains with cultivation configuration.
 * Displays strain information including expected and calculated BE percentages.
 *
 * US-PE-001: Strains Management Page
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageLayout } from '@/components/layout'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import SearchInput from '@/components/ui/search-input'
import { useAllStrains } from '@/hooks/useStrains'
import { Dna, ChevronRight, Plus } from 'lucide-react'

/**
 * Format BE percentage display
 */
function formatBE(value) {
  if (value === null || value === undefined) return 'N/A'
  return `${Number(value).toFixed(0)}%`
}

/**
 * Active status indicator
 */
function ActiveStatus({ active }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm ${active ? 'text-emerald-600' : 'text-gray-400'}`}>
      <span className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-500' : 'bg-gray-300'}`} />
      {active ? 'Active' : 'Inactive'}
    </span>
  )
}

export default function StrainsListPage({ user, onLogout }) {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' })
  const [hideInactive, setHideInactive] = useState(() => {
    const stored = localStorage.getItem('strains-hide-inactive')
    return stored === 'true'
  })

  // Persist toggle state to localStorage
  const handleHideInactiveChange = (checked) => {
    setHideInactive(checked)
    localStorage.setItem('strains-hide-inactive', String(checked))
  }

  const { strains, loading, error } = useAllStrains()

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕'
    return sortConfig.direction === 'asc' ? '↑' : '↓'
  }

  // Filter and sort strains: search filter, optional inactive filter, then sort
  const filteredStrains = useMemo(() => {
    let result = [...strains]

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = result.filter(strain =>
        strain.name?.toLowerCase().includes(search) ||
        strain.code?.toLowerCase().includes(search) ||
        strain.binomial_name?.toLowerCase().includes(search) ||
        strain.common_name?.toLowerCase().includes(search)
      )
    }

    // Apply inactive filter
    if (hideInactive) {
      result = result.filter(strain => strain.active)
    }

    const { key, direction } = sortConfig
    const dir = direction === 'asc' ? 1 : -1

    result.sort((a, b) => {
      // When sorting by status, active-first grouping IS the user-controlled sort
      if (key === 'active') {
        const aActive = a.active ? 1 : 0
        const bActive = b.active ? 1 : 0
        if (aActive !== bActive) return (bActive - aActive) * dir
        return (a.name || '').localeCompare(b.name || '')
      }

      // For all other columns, group active first then sort within groups
      if (a.active && !b.active) return -1
      if (!a.active && b.active) return 1

      // Secondary sort by selected column
      if (key === 'name' || key === 'code') {
        return (a[key] || '').localeCompare(b[key] || '') * dir
      }

      // Numeric columns — nulls last regardless of direction
      const aVal = a[key] != null ? Number(a[key]) : null
      const bVal = b[key] != null ? Number(b[key]) : null
      if (aVal === null && bVal === null) return 0
      if (aVal === null) return 1
      if (bVal === null) return -1
      return (aVal - bVal) * dir
    })

    return result
  }, [strains, searchTerm, hideInactive, sortConfig])

  // Summary statistics
  const stats = useMemo(() => {
    const activeStrains = strains.filter(s => s.active)
    const avgExpectedBE = strains.filter(s => s.expected_yield_percent).reduce((sum, s) => sum + Number(s.expected_yield_percent), 0) / (strains.filter(s => s.expected_yield_percent).length || 1)
    const avgCalculatedBE = strains.filter(s => s.calculated_yield_percent).reduce((sum, s) => sum + Number(s.calculated_yield_percent), 0) / (strains.filter(s => s.calculated_yield_percent).length || 1)

    return {
      total: strains.length,
      active: activeStrains.length,
      avgExpectedBE: strains.filter(s => s.expected_yield_percent).length > 0 ? avgExpectedBE : null,
      avgCalculatedBE: strains.filter(s => s.calculated_yield_percent).length > 0 ? avgCalculatedBE : null
    }
  }, [strains])

  const handleRowClick = (strain) => {
    navigate(`/production/strains/${strain.id}`)
  }

  const handleAddStrain = () => {
    navigate('/production/strains/new')
  }

  if (loading) {
    return (
      <PageLayout
        title="Strains"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          <p className="ml-3 text-muted-foreground">Loading strains...</p>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Strains"
      subtitle={`${stats.active} active strains`}
      headerActions={
        <div className="flex items-center gap-4">
          <SearchInput
            placeholder="Search strains..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          {/* Hide inactive toggle */}
          <div className="flex items-center gap-2">
            <Switch
              checked={hideInactive}
              onCheckedChange={handleHideInactiveChange}
              style={{ backgroundColor: hideInactive ? '#10b981' : '#d1d5db' }}
            />
            <span className="text-sm text-muted-foreground">Hide inactive</span>
          </div>
          <Button onClick={handleAddStrain}>
            <Plus className="h-4 w-4 mr-2" />
            Add Strain
          </Button>
        </div>
      }
      user={user}
      onLogout={onLogout}
    >
      <div className="h-full overflow-y-auto">
        <div className="sales-form-container">

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {filteredStrains.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Dna className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No strains found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm ? 'Try adjusting your search' : 'No strains have been configured'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <Table className="hidden md:table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('code')}>
                      Code {getSortIcon('code')}
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('name')}>
                      Name {getSortIcon('name')}
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('expected_yield_percent')}>
                      Expected Yield {getSortIcon('expected_yield_percent')}
                    </TableHead>
                    <TableHead className="text-right cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('calculated_yield_percent')}>
                      Calculated Yield {getSortIcon('calculated_yield_percent')}
                    </TableHead>
                    <TableHead className="text-center cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('expected_flush_count')}>
                      Flush Count {getSortIcon('expected_flush_count')}
                    </TableHead>
                    <TableHead className="cursor-pointer hover:bg-muted/50 select-none" onClick={() => handleSort('active')}>
                      Status {getSortIcon('active')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStrains.map(strain => (
                    <TableRow
                      key={strain.id}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => handleRowClick(strain)}
                    >
                      <TableCell className="font-mono font-semibold">
                        {strain.code}
                      </TableCell>
                      <TableCell className="font-medium">
                        {strain.name}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatBE(strain.expected_yield_percent)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={strain.calculated_yield_percent ? 'text-blue-600 font-medium' : 'text-muted-foreground'}>
                          {formatBE(strain.calculated_yield_percent)}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {strain.expected_flush_count || '-'}
                      </TableCell>
                      <TableCell>
                        <ActiveStatus active={strain.active} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Mobile Cards */}
              <div className="md:hidden space-y-3">
                {filteredStrains.map(strain => (
                  <Card
                    key={strain.id}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => handleRowClick(strain)}
                  >
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-semibold text-sm">{strain.code}</span>
                          </div>
                          <p className="font-medium mt-1">{strain.name}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      </div>
                      <div className="flex items-center justify-between mt-3 text-sm">
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="text-muted-foreground">Expected: </span>
                            <span className="font-medium">{formatBE(strain.expected_yield_percent)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Actual: </span>
                            <span className={strain.calculated_yield_percent ? 'text-blue-600 font-medium' : 'text-muted-foreground'}>
                              {formatBE(strain.calculated_yield_percent)}
                            </span>
                          </div>
                        </div>
                        <ActiveStatus active={strain.active} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  )
}
